import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';

import { logger } from '../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../events';
import type {
  DataChannelMessage,
  RemoteParticipant,
  RemoteParticipantEvent,
  RemoteParticipantMediaEventHandler,
  RemoteParticipantMediaFailureEventHandler,
} from '../video-sdk';
import { participantsAtom, remoteParticipantToDiagnosticInfo } from '../video-sdk';
import { remoteMediaDelagatorToDiagnosticInfo } from '../video-sdk/twilio';
import { useRemoteAudio } from './useRemoteAudio';
import { useRemoteVideo } from './useRemoteVideo';

export type UseRemoteMediaProps = {
  remoteParticipant: RemoteParticipant;
  onVideoSubscribed?: RemoteParticipantMediaEventHandler;
  onAudioSubscribed?: RemoteParticipantMediaEventHandler;
  onDataTrackSubscribed?: RemoteParticipantMediaEventHandler;
  onVideoUnsubscribed?: RemoteParticipantMediaEventHandler;
  onAudioUnsubscribed?: RemoteParticipantMediaEventHandler;
  onDataTrackUnsubscribed?: RemoteParticipantMediaEventHandler;
  onMessageReceived?: (message: DataChannelMessage) => void;
};

export function useRemoteMedia({
  remoteParticipant,
  onVideoSubscribed,
  onAudioSubscribed,
  onVideoUnsubscribed,
  onAudioUnsubscribed,
}: UseRemoteMediaProps) {
  const remoteParticipantIdentity = remoteParticipant.identity;
  const getRemoteParticipant = useAtomCallback(
    useCallback(
      (get, _set) =>
        get(participantsAtom).find(
          (participant: RemoteParticipant) => participant.identity === remoteParticipantIdentity,
        ),
      [remoteParticipantIdentity],
    ),
  );
  const videoRef = useRemoteVideo({
    remoteParticipant,
    onVideoSubscribed,
    onVideoUnsubscribed,
  });
  const audioRef = useRemoteAudio({
    remoteParticipant,
    onAudioSubscribed,
    onAudioUnsubscribed,
  });

  useEffect(
    function handleGenericMediaListeners() {
      const eventToCallbackMap: {
        mediaSwitchedOff: RemoteParticipantMediaEventHandler;
        mediaSwitchedOn: RemoteParticipantMediaEventHandler;
        mediaSubscriptionFailed: RemoteParticipantMediaFailureEventHandler;
      } = {
        mediaSwitchedOff: (remoteMediaDelegator) => {
          sendLoggingEvents(
            TrackingEvents.REMOTE_TRACK_SWITCHED_OFF,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(remoteMediaDelegator),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            {
              logLevel: 'info',
              message: 'Remote participant track switched off',
            },
          );
        },
        // https://www.twilio.com/docs/video/tutorials/using-bandwidth-profile-api#track-switch-offs
        mediaSwitchedOn: (track) => {
          sendLoggingEvents(
            TrackingEvents.REMOTE_TRACK_SWITCHED_ON,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(track),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            {
              logLevel: 'info',
              message: 'Remote participant track switched on',
            },
          );
        },
        mediaSubscriptionFailed: (error, remoteMediaDelegator) => {
          logger.error(
            `Remote participant track subscription failed`,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(remoteMediaDelegator),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            error,
          );
        },
      };

      const detachEventListenerCallbacks = Object.entries(eventToCallbackMap).map(([event, callback]) => {
        return remoteParticipant.on(event as RemoteParticipantEvent, callback);
      });

      return function cleanup() {
        if (!remoteParticipant) return;
        for (const callback of detachEventListenerCallbacks) {
          callback();
        }
      };
    },
    [remoteParticipant],
  );

  /**
   * Ensures old audio refs are detached from the track
   * Even if its removed without receiving a track unsubscribe event
   * i.e. when switching into screenshare mode
   */
  useEffect(
    function detachAudioRefOnUnmount() {
      const audioElement = audioRef.current;
      return function detachAudioRef() {
        if (!audioElement) return;
        const remoteParticipant = getRemoteParticipant();
        if (!remoteParticipant) return;
        remoteParticipant.detachAudio(audioElement);
      };
    },
    [audioRef, getRemoteParticipant],
  );

  return { videoRef, audioRef };
}
